<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: MediaOverview                                         |
| Section: views/overviews                                         |
| Purpose: Combined photo and document archive with shared search. |
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper overview-wrapper overview-gap-48 overview-wrapper--clip">
        <div class="overview-header overview-header--mobile-start">
            <h1>{{ overviewTitle }}</h1>
            <p class="subtitle overview-header-text">{{ $t('mediaOverview.subtitle') }}</p>

            <div class="category_buttons">
                <button @click="setMediaType('all')" :class="{ active: selectedType === 'all' }">
                    {{ $t('mediaOverview.allMedia') }}
                </button>
                <button @click="setMediaType('photos')" :class="{ active: selectedType === 'photos' }">
                    {{ $t('mediaOverview.photos') }}
                </button>
                <button @click="setMediaType('documents')" :class="{ active: selectedType === 'documents' }">
                    {{ $t('mediaOverview.documents') }}
                </button>
            </div>

            <div :class="['search-box', 'overview-search-box', { focused: isFocused }]" ref="searchBox">
                <div class="search-icon">
                    <img src="@/assets/icons/magnifier.svg" alt="" />
                </div>
                <input type="text" v-model="searchQuery" :placeholder="$t('mediaOverview.searchPlaceholder')"
                    class="search-input" @focus="isFocused = true" @blur="isFocused = false" @input="onSearch" />
                <button type="button" @click="onSearch">
                    <img src="@/assets/icons/search_submit.svg" :alt="$t('mediaOverview.search')" />
                </button>
            </div>
        </div>

        <LoadingSpinner v-if="loading" />

        <p v-else-if="loadError" class="no_results">{{ $t('mediaOverview.loadError') }}</p>
        <p v-else-if="!media.length" class="no_results">{{ $t('mediaOverview.noResults') }}</p>

        <div class="MediaCards media-cards--fixed" v-else>
            <MediaCard v-for="item in media" :key="`${item.mediaType}-${item.mediaID}`"
                :thumbnail="isAccessible(item) ? getThumbnailPath(item) : 'path/to/guest-thumbnail.jpg'"
                :label="item.title || $t('mediaOverview.noTitle')"
                :url="isAccessible(item) ? getFilePath(item) : localePath('/login')"
                :class="{ 'guest-media-card': !isAccessible(item) }"
                @click="isAccessible(item) ? openMediaOverlay(item) : $router.push(localePath('/login'))" />
        </div>

        <ControlWrapper class="control_wrapper--flush" :currentPage="currentPage" :totalPages="totalPages"
            :totalResults="totalResults" :resultsPerPageOptions="[10, 20, 50, 100]"
            :selectedResultsPerPage="resultsPerPage" @update:selectedResultsPerPage="onChangeResultsPerPage"
            @goToPage="onPageChange" @prevPage="onPrevPage" @nextPage="onNextPage" />

        <MediaOverlay v-if="showOverlay" :isVisible="showOverlay" :mediaUrl="overlayMedia?.url"
            :mediaType="overlayMedia?.type" :mediaKind="overlayMedia?.kind" :mediaTitle="overlayMedia?.title"
            :mediaOwner="overlayMedia?.owner"
            :linkedPersonIDs="overlayMedia?.linkedPersonIDs" :mediaList="overlayMediaArray"
            :currentMediaIndex="currentMediaIndex" @close="closeOverlay" @navigate="navigateOverlay" />
    </div>
    <ContactCta />
</template>

<script>
import { debounce } from 'lodash';
import { useRoute } from 'vue-router';
import { fetchMedia } from '@/api/apiService';
import MediaCard from '@/atoms/MediaCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import ControlWrapper from '@/components/ControlWrapper.vue';
import MediaOverlay from '@/components/MediaOverlay.vue';
import ContactCta from '@/components/ContactCta.vue';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { usePagination } from '@/composables/usePagination';
import {
    getDocumentPath,
    getDocumentThumbnailPath,
    getPhotoPath,
    getPhotoThumbnailPath,
    isPublicDocument,
    normalizeOverlayMedia,
} from '@/utils/mediaUtils';
import { applyBreadcrumbSeo, applyGenericSeo } from '@/utils/seoUtils';

const VALID_MEDIA_TYPES = ['all', 'photos', 'documents'];

export default {
    name: 'MediaOverview',
    components: {
        ControlWrapper,
        LoadingSpinner,
        MediaCard,
        MediaOverlay,
        ContactCta,
    },
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        const route = useRoute();
        const pagination = usePagination({
            initialPage: parseInt(route.query.page, 10) || 1,
            initialPerPage: parseInt(route.query.limit, 10) || 20,
        });

        return {
            authToken,
            localePath,
            ...pagination,
        };
    },
    data() {
        return {
            media: [],
            searchQuery: '',
            selectedType: 'all',
            isFocused: false,
            loading: false,
            loadError: false,
            showOverlay: false,
            overlayMedia: null,
            overlayMediaArray: [],
            currentMediaIndex: 0,
            lastRequestId: 0,
            debouncedSearch: null,
        };
    },
    computed: {
        overviewTitle() {
            const titleKey = {
                photos: 'mediaOverview.photoTitle',
                documents: 'mediaOverview.documentTitle',
            }[this.selectedType] || 'mediaOverview.title';

            return this.$t(titleKey, { count: this.totalResults });
        },
    },
    watch: {
        '$route.query.search': 'fetchMediaData',
        '$route.query.type': 'fetchMediaData',
        '$route.query.albumID': 'fetchMediaData',
        '$route.query.category': 'fetchMediaData',
        '$route.query.page': 'fetchMediaData',
        '$route.query.limit': 'fetchMediaData',
    },
    methods: {
        normalizeMediaType(value) {
            return VALID_MEDIA_TYPES.includes(value) ? value : 'all';
        },
        async fetchMediaData() {
            const requestId = ++this.lastRequestId;
            const page = Math.max(parseInt(this.$route.query.page, 10) || 1, 1);
            const limit = Math.max(parseInt(this.$route.query.limit, 10) || 20, 1);
            const search = String(this.$route.query.search || '');
            const type = this.normalizeMediaType(this.$route.query.type);

            this.loading = true;
            this.loadError = false;
            this.searchQuery = search;
            this.selectedType = type;

            try {
                const response = await fetchMedia(
                    page,
                    limit,
                    search,
                    type,
                    this.$route.query.albumID || null,
                    this.$route.query.category || ''
                );
                if (requestId !== this.lastRequestId) return;

                const total = Number(response.totalMedia) || 0;
                const lastPage = Math.max(Math.ceil(total / limit), 1);
                const normalizedPage = Math.min(page, lastPage);

                this.media = response.media || [];
                this.setTotalResults(total);
                this.changeResultsPerPage(limit);
                this.goToPage(normalizedPage);
                this.overlayMediaArray = this.media.filter(this.isAccessible);

                if (normalizedPage !== page) {
                    this.$router.replace({
                        path: this.localePath('/fotos-en-documenten'),
                        query: { ...this.$route.query, page: normalizedPage, limit },
                    });
                }
            } catch (error) {
                if (requestId !== this.lastRequestId) return;
                console.error('Error fetching media:', error);
                this.media = [];
                this.setTotalResults(0);
                this.loadError = true;
            } finally {
                if (requestId === this.lastRequestId) this.loading = false;
            }
        },
        isAccessible(item) {
            if (this.authToken) return true;
            if (item.mediaType === 'documents') return isPublicDocument(item.title);
            return !item.living;
        },
        getFilePath(item) {
            return item.mediaType === 'photos'
                ? getPhotoPath(item.path)
                : getDocumentPath(item.path);
        },
        getThumbnailPath(item) {
            return item.mediaType === 'photos'
                ? getPhotoThumbnailPath(item.thumbpath)
                : getDocumentThumbnailPath(item.thumbpath);
        },
        getOverlayMedia(item) {
            return normalizeOverlayMedia(item, {
                type: item.mediaType === 'photos' ? 'image' : undefined,
                kind: item.mediaType,
                pathResolver: item.mediaType === 'photos' ? getPhotoPath : getDocumentPath,
                defaultTitle: this.$t('mediaOverview.noTitle'),
                defaultOwner: this.$t('mediaOverview.unknownOwner'),
            });
        },
        setMediaType(type) {
            this.debouncedSearch?.cancel();
            this.selectedType = type;
            this.updateRoute(1, type);
        },
        onSearch() {
            this.debouncedSearch();
        },
        buildQuery(page = this.currentPage, type = this.selectedType) {
            const query = { page, limit: this.resultsPerPage };
            if (this.searchQuery.length >= 2) query.search = this.searchQuery;
            if (type !== 'all') query.type = type;

            if (type === 'photos' && this.$route.query.albumID) {
                query.albumID = this.$route.query.albumID;
            }
            if (type === 'documents' && this.$route.query.category) {
                query.category = this.$route.query.category;
            }
            return query;
        },
        updateRoute(page = null, type = this.selectedType) {
            this.$router.push({
                path: this.localePath('/fotos-en-documenten'),
                query: this.buildQuery(page ?? this.currentPage, type),
            });
        },
        onChangeResultsPerPage(limit) {
            this.changeResultsPerPage(limit);
            this.updateRoute(1);
        },
        onPageChange(page) {
            this.goToPage(page);
            this.updateRoute();
        },
        onPrevPage() {
            this.prevPage();
            this.updateRoute();
        },
        onNextPage() {
            this.nextPage();
            this.updateRoute();
        },
        openMediaOverlay(item) {
            if (!this.isAccessible(item)) {
                this.$router.push(this.localePath('/login'));
                return;
            }

            this.overlayMediaArray = this.media.filter(this.isAccessible);
            this.currentMediaIndex = this.overlayMediaArray.findIndex(mediaItem =>
                mediaItem.mediaID === item.mediaID && mediaItem.mediaType === item.mediaType
            );
            this.overlayMedia = this.getOverlayMedia(item);
            this.showOverlay = true;
        },
        closeOverlay() {
            this.showOverlay = false;
        },
        navigateOverlay(newIndex) {
            if (newIndex < 0 || newIndex >= this.overlayMediaArray.length) return;
            this.currentMediaIndex = newIndex;
            this.overlayMedia = this.getOverlayMedia(this.overlayMediaArray[newIndex]);
        },
    },
    created() {
        this.debouncedSearch = debounce(() => this.updateRoute(1), 500);
    },
    mounted() {
        const title = this.$t('mediaOverview.seoTitle');
        const description = this.$t('mediaOverview.seoDescription');
        const image = 'https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg';

        applyGenericSeo({ title, description, image, url: window.location.href }, { includeTwitter: true });
        applyBreadcrumbSeo([
            { name: this.$t('nav.home'), url: `https://sejarah-thumann.online${this.localePath('/')}` },
            {
                name: this.$t('mediaOverview.breadcrumb'),
                url: `https://sejarah-thumann.online${this.localePath('/fotos-en-documenten')}`,
            },
        ]);
        this.fetchMediaData();
    },
    beforeUnmount() {
        this.debouncedSearch?.cancel();
    },
};
</script>
